<!--

  Usage:

  Data is of format: { value : count }.

  <Histogram
    :data="[
      {
        count: ...,
        range: [ 0, 10 ]
      },

      {
        count: ...,
        range: [ 11, 20 ]
      }
    ]"
     groups="10">
    <template slot="title">Histogram Title</template>
  </Histogram>

-->

<template>
  <div class="Histogram">
    <div class="text-large text-center">
      <slot name="title"></slot>
    </div>

    <svg ref="chart"></svg>
  </div>
</template>

<script>
  import * as d3 from 'd3';
  import { colour } from '@/legacy/lib//util';

  export default {
    props: {
      title: {
        type: String,
        default: 'Value'
      },

      data: {
        type: Array
      }
    },

    mounted: function () {
      let chart = this.$refs.chart;

      let margin = {
        top:    30,
        right:  30,
        bottom: 60,
        left:   80
      };

      let boundingBox = chart.getBoundingClientRect ();

      let width  = boundingBox.width - margin.left - margin.right;
      let height = boundingBox.height - margin.top - margin.bottom;

      let svg = d3
        .select (chart)
        .attr ('width', width + margin.left + margin.right)
        .attr ('height', height + margin.top + margin.bottom)
        .append ('g')
        .attr ('transform', `translate(${margin.left}, ${margin.top})`);

      // let data = this.data.filter (d => d.count > 0);
      let data = this.data;
      let xMax = data [data.length - 1].range [1];

      let x = d3
        .scaleLinear ()
        .domain ([ 0, xMax ])
        .range ([ 0, width ])
        .nice ();

      let histogram = d3
        .histogram ()
        .value (d => d)
        .domain (x.domain ())
        .thresholds (x.ticks (10));

      let bins = histogram (this.generate);

      let yMin = 0;
      let yMax = d3.max (bins, d => d.length);

      let y = d3
        .scaleLinear ()
        .range ([ height, 0 ])
        .domain ([ yMin, yMax ])
        .nice ();

      let interpolate = d3
        .scaleLinear ()
        .domain ([ yMin, yMax ])
        .interpolate (d3.interpolateHcl)

        .range ([
            d3.rgb (colour ('red')),
            d3.rgb (colour ('green'))
        ]);

      svg
        .append ('g')
        .call (d3.axisLeft (y));

      svg
        .append ('g')
        .attr ('transform', `translate(0, ${height})`)
        .call (d3.axisBottom (x));

      let bar = svg
        .selectAll ('.Bar')
        .data (bins)
        .enter ()
        .append ('g')
        .attr ('class', 'Bar')
        .attr ('transform', d => `translate(${x (d.x0)}, ${y (d.length)})`)
        .style ('display', d => d.length <= 0 ? 'none' : '');

      bar
        .append ('rect')
        .attr ('x', 1)
        .attr ('width', d => x (d.x1) - x (d.x0))
        .attr ('height', d => height - (y (d.length)))
        .style ('fill', d => interpolate (d.length));

      bar
        .append ('text')
        .attr ('dy', '.75em')
        .attr ('y', -24)
        .attr ('x', d => (x (d.x1) - x (d.x0)) / 2)
        .attr ('text-anchor', 'middle')
        .text (d => d.length);

      svg
        .append ('text')
        .attr ('transform', `translate(${width / 2}, ${height + margin.top + 20})`)
        .attr ('class', 'Label')
        .style ('text-anchor', 'middle')
        .text (this.title);

      svg
        .append ('text')
        .attr ('transform', 'rotate(-90)')
        .attr ('y', -margin.left)
        .attr ('x', -(height / 2))
        .attr ('dy', '1em')
        .attr ('class', 'Label')
        .style ('text-anchor', 'middle')
        .text ('# of times');
    },

    computed: {
      generate () {
        let data = [];

        for (let counter of this.data) {
          let lower = counter.range [0];
          let upper = counter.range [1];

          let midpoint = lower + ((upper - lower) / 2);

          data = data.concat (
            new Array (counter.count).fill (midpoint)
          );
        }

        return data;
      }
    }
  };
</script>

<style lang="scss">
  .Histogram {
    svg {
      height: 100%;
      width: 100%;
    }

    text {
      fill: rgba(var(--white), 1);
    }
  }
</style>